/**
 * Partidas online: salas, amigos e o bate-papo antes da batalha.
 *
 * A divisão de trabalho é a mesma do servidor: aqui se combina — quem joga com
 * quem, em que mapa, com que relógio — e a partida em si é jogada pela
 * escaramuça, com o mesmo motor de regras de sempre.
 *
 * A tela se atualiza perguntando de tempos em tempos, e não por WebSocket. Uma
 * sala é um combinado de poucos segundos entre duas pessoas: uma pergunta a
 * cada três segundos custa menos, em código e em coisa para dar errado, do que
 * manter uma conexão viva para descobrir que alguém marcou "pronto".
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, messageOf } from '../api/client.js';
import { ASSETS_BASE } from '../assets/manifest.js';
import { useAuth } from '../auth/AuthContext.js';

interface Player {
  readonly id: number;
  readonly username: string;
  readonly avatarPortrait: string;
  readonly ready?: boolean;
}

interface Lobby {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly mapId: number;
  readonly visibility: 'publica' | 'privada' | 'amigos';
  readonly status: string;
  readonly turnSeconds: number;
  readonly ranked: boolean;
  readonly host: Player;
  readonly guest: Player | null;
  readonly isHost: boolean;
  readonly isGuest: boolean;
  readonly seed?: number;
}

interface ChatLine {
  readonly id: number;
  readonly kind: 'jogador' | 'sistema';
  readonly username: string | null;
  readonly body: string;
  readonly createdAt: string;
}

interface Friend {
  readonly id: number;
  readonly username: string;
  readonly avatarPortrait: string;
  readonly presence: 'online' | 'em_partida' | 'ausente' | 'offline';
  readonly elo: number;
}

interface Found {
  readonly id: number;
  readonly username: string;
  readonly avatarPortrait: string;
  readonly elo: number;
  readonly friendship: string | null;
}

interface MapChoice {
  readonly id: number;
  readonly name: string;
  readonly width: number;
  readonly height: number;
}

const POLL_MS = 3000;
const PRESENCE_MS = 30_000;

const PRESENCE_LABEL: Record<Friend['presence'], string> = {
  online: 'disponível',
  em_partida: 'em partida',
  ausente: 'ausente',
  offline: 'offline',
};

function Avatar({ portrait, size = 34 }: { portrait: string; size?: number }): JSX.Element {
  return (
    <img
      className="lobby-avatar"
      src={`${ASSETS_BASE}/img/faces/${portrait}.webp`}
      alt=""
      style={{ width: size, height: size }}
      // Uma folha de retratos tem oito rostos; mostrar o primeiro já identifica
      // a pessoa, e recortar cada um custaria mais do que entrega aqui.
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
    />
  );
}

export function LobbyPage({
  onPlayLobby,
  onOpenAuth,
}: {
  readonly onPlayLobby: (lobby: Lobby) => void;
  readonly onOpenAuth: () => void;
}): JSX.Element {
  const { user } = useAuth();
  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [current, setCurrent] = useState<Lobby | null>(null);
  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [incoming, setIncoming] = useState<Array<{ id: number; username: string }>>([]);
  const [invites, setInvites] = useState<Array<{ lobbyId: number; code: string; name: string; from: Player }>>([]);
  const [maps, setMaps] = useState<MapChoice[]>([]);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<Found[]>([]);
  const [draft, setDraft] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');
  const [side, setSide] = useState<'salas' | 'amigos'>('salas');

  // Formulário de nova sala
  const [newMapId, setNewMapId] = useState(0);
  const [newName, setNewName] = useState('');
  const [newVisibility, setNewVisibility] = useState<Lobby['visibility']>('publica');
  const [newTurnSeconds, setNewTurnSeconds] = useState(0);

  const lastMessageRef = useRef(0);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .get<{ maps: MapChoice[] }>('/api/maps')
      .then((data) => {
        setMaps(data.maps);
        if (data.maps.length > 0) setNewMapId(data.maps[0]!.id);
      })
      .catch(() => setMaps([]));
  }, []);

  /** "Estou aqui", para os amigos saberem quem convidar. */
  useEffect(() => {
    if (!user) return;

    const beat = () => {
      void api.post('/api/online/presenca', { status: current ? 'em_partida' : 'online' })
        .catch(() => undefined);
    };

    beat();
    const timer = window.setInterval(beat, PRESENCE_MS);
    return () => window.clearInterval(timer);
  }, [user, current]);

  const refreshLists = useCallback(async () => {
    if (!user) return;
    try {
      const [rooms, people, pending] = await Promise.all([
        api.get<{ lobbies: Lobby[] }>('/api/online/salas'),
        api.get<{ friends: Friend[]; incoming: Array<{ id: number; username: string }> }>('/api/online/amigos'),
        api.get<{ invites: Array<{ lobbyId: number; code: string; name: string; from: Player }> }>('/api/online/convites'),
      ]);
      setLobbies(rooms.lobbies);
      setFriends(people.friends);
      setIncoming(people.incoming);
      setInvites(pending.invites);
    } catch (e) {
      setError(messageOf(e, 'Não consegui atualizar a lista.'));
    }
  }, [user]);

  /** Enquanto se está numa sala, o que importa é a sala. */
  const refreshLobby = useCallback(async (id: number) => {
    try {
      const data = await api.get<{ lobby: Lobby; messages: ChatLine[] }>(
        `/api/online/salas/${id}?since=${lastMessageRef.current}`,
      );
      setCurrent(data.lobby);

      if (data.messages.length > 0) {
        lastMessageRef.current = data.messages[data.messages.length - 1]!.id;
        setMessages((old) => [...old, ...data.messages].slice(-200));
      }

      // Os dois prontos: a partida começa.
      if (data.lobby.status === 'pronta' && (data.lobby.isHost || data.lobby.isGuest)) {
        onPlayLobby(data.lobby);
      }
    } catch (e) {
      setError(messageOf(e, 'Perdi o contato com a sala.'));
    }
  }, [onPlayLobby]);

  useEffect(() => {
    if (!user) return;

    const tick = () => {
      if (current) void refreshLobby(current.id);
      else void refreshLists();
    };

    tick();
    const timer = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(timer);
  }, [user, current?.id, refreshLists, refreshLobby]);

  useEffect(() => {
    const box = chatRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setFound([]);
      return;
    }

    // Espera a digitação parar: uma busca por tecla é uma consulta ao banco por
    // tecla, e o resultado da penúltima letra nunca é o que se queria ver.
    const timer = window.setTimeout(() => {
      void api
        .get<{ players: Found[] }>(`/api/online/jogadores?q=${encodeURIComponent(search.trim())}`)
        .then((data) => setFound(data.players))
        .catch(() => setFound([]));
    }, 350);

    return () => window.clearTimeout(timer);
  }, [search]);

  const act = async (run: () => Promise<unknown>, after?: () => void) => {
    setError('');
    try {
      await run();
      after?.();
      await refreshLists();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const enter = async (id: number, code?: string) => {
    setError('');
    try {
      const result = await api.post<{ id: number }>('/api/online/salas/entrar',
        code ? { code } : { lobbyId: id });
      lastMessageRef.current = 0;
      setMessages([]);
      await refreshLobby(result.id);
    } catch (e) {
      setError(messageOf(e, 'Não consegui entrar na sala.'));
    }
  };

  if (!user) {
    return (
      <section data-panel="online">
        <h2>🌐 Partidas Online</h2>
        <p className="muted">
          <button type="button" className="linklike" onClick={onOpenAuth}>Entre na sua conta</button>
          {' '}para criar salas, convidar amigos e jogar contra outras pessoas.
        </p>
      </section>
    );
  }

  // ----- dentro de uma sala -------------------------------------------------
  if (current) {
    const me = current.isHost ? current.host : current.guest;
    const other = current.isHost ? current.guest : current.host;
    const mapName = maps.find((m) => m.id === current.mapId)?.name ?? `Mapa #${current.mapId}`;

    return (
      <section data-panel="online" className="lobby-room">
        <header className="lobby-room-head">
          <div>
            <h2>{current.name}</h2>
            <p className="muted">
              {mapName} · código <code className="lobby-code">{current.code}</code>
              {current.turnSeconds > 0 && <> · {current.turnSeconds}s por turno</>}
              {current.ranked && <> · vale ranking</>}
            </p>
          </div>
          <button
            type="button"
            className="lobby-leave"
            onClick={() => void act(
              () => api.post('/api/online/salas/sair', { lobbyId: current.id }),
              () => { setCurrent(null); setMessages([]); lastMessageRef.current = 0; },
            )}
          >
            Sair da sala
          </button>
        </header>

        {error && <p className="importer-error">⚠️ {error}</p>}

        <div className="lobby-seats">
          {[current.host, current.guest].map((seat, index) => (
            <div key={index} className={`lobby-seat ${seat?.ready ? 'ready' : ''}`}>
              {seat ? (
                <>
                  <Avatar portrait={seat.avatarPortrait} size={48} />
                  <div>
                    <strong>{seat.username}</strong>
                    <span className="lobby-seat-role">{index === 0 ? 'anfitrião' : 'convidado'}</span>
                  </div>
                  <span className={`lobby-ready ${seat.ready ? 'on' : ''}`}>
                    {seat.ready ? '✅ pronto' : 'aguardando'}
                  </span>
                </>
              ) : (
                <span className="lobby-empty">à espera de um adversário…</span>
              )}
            </div>
          ))}
        </div>

        <div className="lobby-actions">
          <button
            type="button"
            className={`lobby-ready-btn ${me?.ready ? 'on' : ''}`}
            disabled={!other}
            title={other ? undefined : 'Espere alguém entrar na sala'}
            onClick={() => void act(() => api.post('/api/online/salas/pronto', {
              lobbyId: current.id, ready: !me?.ready,
            }))}
          >
            {me?.ready ? '✔ Pronto (clique para desmarcar)' : 'Estou pronto'}
          </button>

          {current.isHost && !other && (
            <div className="lobby-invite-row">
              <span className="muted">Convidar amigo:</span>
              {friends.filter((f) => f.presence !== 'offline').map((friend) => (
                <button
                  key={friend.id}
                  type="button"
                  className="lobby-invite-btn"
                  onClick={() => void act(() => api.post('/api/online/salas/convidar', {
                    lobbyId: current.id, userId: friend.id,
                  }))}
                >
                  + {friend.username}
                </button>
              ))}
              {friends.filter((f) => f.presence !== 'offline').length === 0 && (
                <span className="muted">nenhum amigo online agora</span>
              )}
            </div>
          )}
        </div>

        <div className="lobby-chat">
          <div className="lobby-chat-log" ref={chatRef}>
            {messages.map((line) => (
              <p key={line.id} className={line.kind === 'sistema' ? 'chat-system' : ''}>
                {line.kind === 'jogador' && <strong>{line.username}: </strong>}
                {line.body}
              </p>
            ))}
            {messages.length === 0 && <p className="muted">Sem mensagens ainda.</p>}
          </div>
          <form
            className="lobby-chat-form"
            onSubmit={(event) => {
              event.preventDefault();
              const body = draft.trim();
              if (body === '') return;
              setDraft('');
              void act(() => api.post('/api/online/salas/mensagem', { lobbyId: current.id, body }));
            }}
          >
            <input
              type="text"
              value={draft}
              maxLength={500}
              placeholder="Escreva para o seu adversário…"
              onChange={(event) => setDraft(event.target.value)}
            />
            <button type="submit">Enviar</button>
          </form>
        </div>
      </section>
    );
  }

  // ----- fora de qualquer sala ---------------------------------------------
  return (
    <section data-panel="online" className="lobby-shell">
      <header className="lobby-head">
        <h2>🌐 Partidas Online</h2>
        <nav className="shop-tabs">
          <button type="button" className={`shop-tab ${side === 'salas' ? 'active' : ''}`} onClick={() => setSide('salas')}>
            🎮 Salas
          </button>
          <button type="button" className={`shop-tab ${side === 'amigos' ? 'active' : ''}`} onClick={() => setSide('amigos')}>
            👥 Amigos {incoming.length > 0 && <span className="badge">{incoming.length}</span>}
          </button>
        </nav>
      </header>

      {error && <p className="importer-error">⚠️ {error}</p>}

      {invites.length > 0 && (
        <div className="lobby-invites">
          {invites.map((invite) => (
            <p key={invite.lobbyId}>
              <strong>{invite.from.username}</strong> convidou você para “{invite.name}”.
              <button type="button" onClick={() => void enter(invite.lobbyId)}>Entrar</button>
            </p>
          ))}
        </div>
      )}

      {side === 'salas' ? (
        <>
          <div className="lobby-create">
            <h3>Abrir uma sala</h3>
            <div className="lobby-create-row">
              <label>
                <span>Mapa</span>
                <select value={newMapId} onChange={(e) => setNewMapId(Number(e.target.value))}>
                  {maps.map((map) => (
                    <option key={map.id} value={map.id}>
                      {map.name} ({map.width}×{map.height})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Nome</span>
                <input
                  type="text"
                  value={newName}
                  maxLength={96}
                  placeholder={`Sala de ${user.username}`}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <label>
                <span>Quem vê</span>
                <select
                  value={newVisibility}
                  onChange={(e) => setNewVisibility(e.target.value as Lobby['visibility'])}
                >
                  <option value="publica">Qualquer um</option>
                  <option value="amigos">Só amigos</option>
                  <option value="privada">Só com o código</option>
                </select>
              </label>
              <label>
                <span>Relógio</span>
                <select value={newTurnSeconds} onChange={(e) => setNewTurnSeconds(Number(e.target.value))}>
                  <option value={0}>Sem relógio</option>
                  <option value={60}>60s por turno</option>
                  <option value={90}>90s por turno</option>
                  <option value={180}>3min por turno</option>
                </select>
              </label>
              <button
                type="button"
                className="lobby-create-btn"
                onClick={() => void act(async () => {
                  const created = await api.post<{ id: number }>('/api/online/salas', {
                    mapId: newMapId, name: newName, visibility: newVisibility,
                    turnSeconds: newTurnSeconds,
                  });
                  lastMessageRef.current = 0;
                  setMessages([]);
                  await refreshLobby(created.id);
                })}
              >
                Abrir sala
              </button>
            </div>

            <div className="lobby-join">
              <span>ou entre com um código:</span>
              <input
                type="text"
                value={joinCode}
                maxLength={8}
                placeholder="ABC123"
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              />
              <button type="button" disabled={joinCode.length < 4} onClick={() => void enter(0, joinCode)}>
                Entrar
              </button>
            </div>
          </div>

          <h3>Salas abertas</h3>
          {lobbies.length === 0 ? (
            <p className="muted">Nenhuma sala aberta agora. Abra a sua acima.</p>
          ) : (
            <ul className="lobby-list">
              {lobbies.map((lobby) => (
                <li key={lobby.id} className="lobby-row">
                  <Avatar portrait={lobby.host.avatarPortrait} />
                  <div className="lobby-row-body">
                    <strong>{lobby.name}</strong>
                    <span className="muted">
                      {lobby.host.username} · {maps.find((m) => m.id === lobby.mapId)?.name ?? `Mapa #${lobby.mapId}`}
                      {lobby.turnSeconds > 0 && <> · {lobby.turnSeconds}s</>}
                      {lobby.guest && <> · 2/2</>}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={lobby.guest !== null && !lobby.isHost && !lobby.isGuest}
                    onClick={() => void enter(lobby.id)}
                  >
                    {lobby.isHost || lobby.isGuest ? 'Voltar' : lobby.guest ? 'cheia' : 'Entrar'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <div className="friend-search">
            <input
              type="search"
              value={search}
              placeholder="Procurar jogador pelo nome…"
              onChange={(e) => setSearch(e.target.value)}
            />
            {found.length > 0 && (
              <ul className="friend-results">
                {found.map((person) => (
                  <li key={person.id}>
                    <Avatar portrait={person.avatarPortrait} />
                    <span>{person.username}</span>
                    <span className="muted">★ {person.elo}</span>
                    {person.friendship === 'aceito' ? (
                      <span className="muted">já é amigo</span>
                    ) : person.friendship === 'pendente' ? (
                      <span className="muted">convite pendente</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void act(
                          () => api.post('/api/online/amigos/adicionar', { userId: person.id }),
                          () => setSearch(''),
                        )}
                      >
                        + Adicionar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {incoming.length > 0 && (
            <>
              <h3>Convites recebidos</h3>
              <ul className="friend-list">
                {incoming.map((person) => (
                  <li key={person.id}>
                    <span>{person.username}</span>
                    <button
                      type="button"
                      onClick={() => void act(() => api.post('/api/online/amigos/responder', {
                        userId: person.id, accept: true,
                      }))}
                    >
                      Aceitar
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => void act(() => api.post('/api/online/amigos/responder', {
                        userId: person.id, accept: false,
                      }))}
                    >
                      Recusar
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3>Seus amigos</h3>
          {friends.length === 0 ? (
            <p className="muted">Você ainda não adicionou ninguém. Procure pelo nome acima.</p>
          ) : (
            <ul className="friend-list">
              {friends.map((friend) => (
                <li key={friend.id} className={`presence-${friend.presence}`}>
                  <Avatar portrait={friend.avatarPortrait} />
                  <span className="friend-name">{friend.username}</span>
                  <span className={`presence-dot ${friend.presence}`} />
                  <span className="muted">{PRESENCE_LABEL[friend.presence]} · ★ {friend.elo}</span>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => void act(() => api.delete(`/api/online/amigos/${friend.id}`))}
                  >
                    Remover
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

export type { Lobby };
